import { onMounted, onUnmounted, ref, type Ref } from 'vue'
import { getScrollParent } from '@/core/utils/get-scroll-parent'

/**
 * Tracks whether the scroll container of `elementRef` is scrolled into its
 * bottom region. Used to "focus" the final part of a long question by moving
 * it into view once the user reaches the end of the list.
 */
export function useScrollFocusAtBottom(
  elementRef: Ref<HTMLElement | null>,
  // How many pixels before the absolute bottom the focus should kick in.
  threshold: number = 48,
) {
  const isAtBottom = ref(false)
  const scrollParent = ref<HTMLElement | null>(null)

  const update = () => {
    const el = scrollParent.value
    if (!el) return
    const isScrollable = el.scrollHeight - el.clientHeight > threshold
    isAtBottom.value = isScrollable && el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
  }

  onMounted(() => {
    if (!elementRef.value) return

    scrollParent.value = getScrollParent(elementRef.value)
    if (!scrollParent.value) return

    scrollParent.value.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    update()
  })

  onUnmounted(() => {
    scrollParent.value?.removeEventListener('scroll', update)
    window.removeEventListener('resize', update)
  })

  return {
    isAtBottom,
    scrollParent,
  }
}
